import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, CircularProgress, Container, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { OffersList } from '../components';
import { useSpecialistOffersStore } from '../services';
import { OfferStatus } from '../types';

// Section loading component
const SectionLoader = () => {
	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '50vh',
				width: '100%',
				flexDirection: 'column',
			}}
		>
			<CircularProgress size={50} sx={{ color: '#3da08f' }} />
			<Box sx={{ mt: 2, color: 'text.secondary' }}>Loading...</Box>
		</Box>
	);
};

export const OffersListPage: React.FC = () => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const navigate = useNavigate();
	const location = useLocation();
	const [isPageLoading, setIsPageLoading] = useState(true);
	const [currentStatus, setCurrentStatus] = useState<OfferStatus>('active');
	const statusChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useAppbarTitle('requestsAndOffers.oneTimeSeasonal');
	usePageTitle(t('requestsAndOffers.oneTimeSeasonal'));

	const { status, setStatus, resetState, isLoading } = useSpecialistOffersStore();

	const [searchParams, setSearchParams] = useSearchParams();

	// Reset to active status when component mounts or when navigating back to this page
	useEffect(() => {
		console.log('[OffersListPage] Component mounted or location changed');
		setIsPageLoading(true);
		
		// Check if there's a status in the URL
		const statusParam = searchParams.get('status') as OfferStatus | null;
		
		if (statusParam && ['active', 'archived', 'completed'].includes(statusParam)) {
			console.log(`[OffersListPage] Setting status from URL: ${statusParam}`);
			setCurrentStatus(statusParam);
			setStatus(statusParam);
		} else {
			// Default to active if no valid status in URL
			console.log('[OffersListPage] No valid status in URL, defaulting to active');
			setCurrentStatus('active');
			setStatus('active');
			
			// Update URL to reflect active status
			const newParams = new URLSearchParams(searchParams);
			newParams.set('status', 'active');
			setSearchParams(newParams);
		}
		
		// Show page content after a short delay
		const timer = setTimeout(() => {
			setIsPageLoading(false);
		}, 300);
		
		// Clean up when leaving the page
		return () => {
			console.log('[OffersListPage] Component unmounting');
			resetState();
			clearTimeout(timer);
			
			if (statusChangeTimeoutRef.current) {
				clearTimeout(statusChangeTimeoutRef.current);
			}
		};
	}, [location.key]); // This will run when navigating back to this page

	// Monitor loading state from store
	useEffect(() => {
		console.log(`[OffersListPage] Store loading state changed: ${isLoading}`);
	}, [isLoading]);

	const handleStatusChange = (event: SelectChangeEvent) => {
		const newStatus = event.target.value as OfferStatus;
		console.log(`[OffersListPage] Status changed to: ${newStatus}`);
		
		// Show loading state
		setIsPageLoading(true);
		setCurrentStatus(newStatus);
		
		// Update URL
		const newParams = new URLSearchParams(searchParams);
		newParams.set('status', newStatus);
		setSearchParams(newParams);
		
		// Update store
		setStatus(newStatus);
		
		// Clear any existing timeout
		if (statusChangeTimeoutRef.current) {
			clearTimeout(statusChangeTimeoutRef.current);
		}
		
		// Hide loading after a short delay
		statusChangeTimeoutRef.current = setTimeout(() => {
			setIsPageLoading(false);
			statusChangeTimeoutRef.current = null;
		}, 300);
	};

	const handleGoBack = () => {
		navigateWithLanguage('/specialist/view-offers');
	};

	return (
		<Container maxWidth="lg">
			<Box className="py-6">
				{/* Header section is always visible */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
					<Button
						className="w-[120px]"
						onClick={handleGoBack}
						variant="text"
						sx={{ backgroundColor: 'white', color: 'black' }}
						startIcon={<Icon path={mdiArrowLeft} size={1} />}
					>
						{t('back')}
					</Button>
					
					{/* Status Filter - Always visible */}
					<FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
						<InputLabel id="status-select-label">{t('status')}</InputLabel>
						<Select
							labelId="status-select-label"
							value={currentStatus}
							onChange={handleStatusChange}
							label={t('status')}
							disabled={isLoading || isPageLoading}
							startAdornment={isLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
						>
							<MenuItem value="active">{t('ongoing', 'Ongoing')}</MenuItem>
							<MenuItem value="archived">{t('archived', 'Archived')}</MenuItem>
							<MenuItem value="completed">{t('completed')}</MenuItem>
						</Select>
					</FormControl>
				</Box>
				
				{/* Show loader when page is loading */}
				{isPageLoading ? (
					<SectionLoader />
				) : (
					/* Show content when not loading */
					<OffersList 
						status={currentStatus} 
						onStatusChange={(newStatus) => {
							console.log(`[OffersListPage] Status change requested from OffersList: ${newStatus}`);
							handleStatusChange({ target: { value: newStatus } } as SelectChangeEvent);
						}} 
					/>
				)}
			</Box>
		</Container>
	);
};
